import {
  BookOpen,
  CheckSquare,
  Eye,
  Lightbulb,
  Rocket,
  Compass,
  GraduationCap,
  Layers,
  Share2,
  UserCog,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import ExportButtons from "./ExportButtons";
import CopyLessonButton from "./CopyLessonButton";
import ShareLessonDialog from "./ShareLessonDialog";

const SectionHeading = ({ icon: Icon, label, testid }) => (
  <div className="flex items-center gap-3 mb-4" data-testid={testid}>
    <div className="h-9 w-9 rounded-lg bg-[#f4f5f7] border border-[#4a6984]/15 grid place-items-center">
      <Icon className="h-4 w-4 text-[#4a6984]" strokeWidth={2} />
    </div>
    <h3 className="font-extrabold text-lg sm:text-xl text-[#4a6984] tracking-tight">
      {label}
    </h3>
  </div>
);

const TagList = ({ items, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {items.map((t, i) => (
      <span
        key={`${t}-${i}`}
        className="ae-pill"
        data-testid={`${testid}-item-${i}`}
      >
        {t}
      </span>
    ))}
  </div>
);

export const ActivityResultCard = ({ activity }) => {
  const [shareOpen, setShareOpen] = useState(false);
  if (!activity) return null;

  return (
    <article
      data-testid="activity-result-card"
      className="ae-card shadow-xl shadow-[#4a6984]/5 p-6 md:p-12"
    >
      {/* Header */}
      <header className="border-b border-[#4a6984]/10 pb-8 mb-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <p className="font-bold uppercase tracking-[0.25em] text-xs text-[#4a6984]/70">
              Generated Activity
            </p>
            <h2
              data-testid="activity-name"
              className="font-black text-3xl sm:text-4xl text-[#4a6984] mt-3 tracking-tight"
            >
              {activity.activity_name}
            </h2>
            {activity.tagline && (
              <p
                data-testid="activity-tagline"
                className="mt-3 text-[#4a6984]/80 font-medium text-base sm:text-lg max-w-2xl"
              >
                {activity.tagline}
              </p>
            )}
          </div>
          {activity.id && (
            <div className="shrink-0">
              <p className="font-bold uppercase text-[10px] tracking-[0.25em] text-[#4a6984]/60 mb-2">
                Export
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <ExportButtons activityId={activity.id} activityName={activity.activity_name} hasScript={!!activity.teacher_script_markdown} hasHandout={!activity.output_type?.startsWith("math_") && /Student Handout|Exit Ticket/i.test(activity.lesson_markdown || activity.unit_markdown || "")} hasSkillEvidence={Array.isArray(activity.student_sheets) && activity.student_sheets.some((s) => s && (s.sheet_type === "skill_exit_ticket" || s._skillsfacilitator_exit_ticket))} hasReading={!!activity.reading_markdown} readingTextType={activity.reading_text_type || null} size="sm" />
                <CopyLessonButton activity={activity} size="sm" testid="activity-copy" />
                {(activity.output_type || "").toLowerCase() === "unit" && activity.id && (
                  <Link
                    to={`/individualize-unit?unit=${encodeURIComponent(activity.id)}`}
                    data-testid="activity-individualize-unit-btn"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1c2d3d] bg-white border border-[#4a6984]/20 hover:bg-[#96ded1]/15 px-3 py-1.5 rounded-full transition"
                    title="Generate a per-lesson student pathway for this unit"
                  >
                    <UserCog className="h-3.5 w-3.5" /> Individualize
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  data-testid="activity-share-public-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1c2d3d] bg-[#96ded1] hover:bg-[#7fd0c1] px-3 py-1.5 rounded-full transition"
                  title="Create a public link to post on social media"
                >
                  <Share2 className="h-3.5 w-3.5" /> Share
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Overview */}
      <section className="mb-10">
        <SectionHeading icon={BookOpen} label="Overview" testid="section-overview" />
        <p
          data-testid="activity-overview"
          className="text-[#4a6984] font-medium leading-relaxed text-base"
        >
          {activity.overview}
        </p>
      </section>

      {/* Skills + Learning skills (two-column on md) */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
        <div>
          <SectionHeading icon={Layers} label="Transferable Skills" testid="section-transferable" />
          <TagList items={activity.transferable_skills || []} testid="transferable-skills" />
        </div>
        <div>
          <SectionHeading
            icon={GraduationCap}
            label="Ontario Learning Skills"
            testid="section-learning-skills"
          />
          <TagList
            items={activity.ontario_learning_skills || []}
            testid="ontario-learning-skills"
          />
        </div>
      </section>

      {/* Curriculum links */}
      <section className="mb-10">
        <SectionHeading icon={Compass} label="Curriculum Links" testid="section-curriculum" />
        <ul className="space-y-2" data-testid="curriculum-links">
          {(activity.curriculum_links || []).map((c, i) => (
            <li
              key={i}
              data-testid={`curriculum-link-${i}`}
              className="flex items-start text-[#4a6984] font-medium"
            >
              <span className="ae-marker" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Facilitation Moves */}
      <section className="mb-10">
        <SectionHeading icon={Rocket} label="Facilitation Moves" testid="section-moves" />
        <ol className="space-y-5" data-testid="facilitation-moves">
          {(activity.facilitation_moves || []).map((m, i) => (
            <li
              key={i}
              data-testid={`facilitation-move-${i}`}
              className="rounded-xl border border-[#4a6984]/10 bg-[#f4f5f7]/60 p-5"
            >
              <div className="flex gap-4">
                <div className="shrink-0 h-7 w-7 rounded-md bg-[#96ded1] text-[#1c2d3d] grid place-items-center font-extrabold text-sm">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[#4a6984] leading-relaxed">{m.step}</p>
                  {m.teacher_tip && (
                    <p className="mt-2 text-sm text-[#4a6984]/80 italic">
                      <span className="font-bold not-italic">Teacher tip:</span>{" "}
                      {m.teacher_tip}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Checklist + Observation prompts */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
        <div>
          <SectionHeading icon={CheckSquare} label="Teacher Checklist" testid="section-checklist" />
          <ul className="space-y-2" data-testid="teacher-checklist">
            {(activity.teacher_checklist || []).map((c, i) => (
              <li key={i} className="flex items-start text-[#4a6984] font-medium">
                <span className="ae-marker" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <SectionHeading icon={Eye} label="Observation Prompts" testid="section-observation" />
          <ul className="space-y-2" data-testid="observation-prompts">
            {(activity.observation_prompts || []).map((c, i) => (
              <li key={i} className="flex items-start text-[#4a6984] font-medium">
                <span className="ae-marker" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Differentiation + Extension */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div
          data-testid="differentiation-tip"
          className="rounded-xl border border-[#4a6984]/10 p-5 bg-[#f4f5f7]/60"
        >
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-4 w-4 text-[#4a6984]" />
            <p className="font-bold uppercase text-xs tracking-[0.2em] text-[#4a6984]/70">
              Differentiation
            </p>
          </div>
          <p className="text-[#4a6984] font-medium leading-relaxed">
            {activity.differentiation_tip}
          </p>
        </div>
        <div
          data-testid="extension-idea"
          className="rounded-xl border border-[#96ded1]/60 p-5 bg-[#96ded1]/10"
        >
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="h-4 w-4 text-[#1c2d3d]" />
            <p className="font-bold uppercase text-xs tracking-[0.2em] text-[#1c2d3d]/70">
              Extension Idea
            </p>
          </div>
          <p className="text-[#1c2d3d] font-medium leading-relaxed">
            {activity.extension_idea}
          </p>
        </div>
      </section>
      {activity.id && (
        <ShareLessonDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          activityId={activity.id}
          activityName={activity.activity_name}
          testid="activity-share"
        />
      )}
    </article>
  );
};

export default ActivityResultCard;
